// Enriched, hand-tuned content for the top 10 service states.
// Rendered by src/routes/states.$state.tsx when a matching entry exists.

export type StateHighlight = {
  slug: string;
  headline: string;
  intro: string; // 2-3 sentence localized intro
  stats: { label: string; value: string }[];
  topMetros: { name: string; blurb: string }[];
  industries: {
    slug:
      | "offices-corporate"
      | "warehouses-manufacturing"
      | "hospitals-clinics"
      | "schools-universities"
      | "hotels-hospitality"
      | "gyms-fitness-centers"
      | "military-bases"
      | "airports-aviation";
    label: string;
    text: string;
  }[];
  extraFaqs: { q: string; a: string }[];
};

export const STATE_HIGHLIGHTS: Record<string, StateHighlight> = {
  california: {
    slug: "california",
    headline: "California's #1 Free Vending Machine Program",
    intro:
      "From Silicon Valley tech campuses to LA logistics hubs and San Diego biotech parks, California businesses trust us for zero-cost snack, drink, coffee, and healthy vending. Routes run daily across the Bay Area, Greater LA, Sacramento, and the Central Valley.",
    stats: [
      { label: "Metros served", value: "40+" },
      { label: "Machines placed statewide", value: "1,800+" },
      { label: "Avg. install window", value: "5–9 days" },
    ],
    topMetros: [
      { name: "Los Angeles", blurb: "Warehouses, studios, and offices from Downtown LA to the SFV and South Bay." },
      { name: "San Francisco", blurb: "SoMa startups, Financial District towers, and Mission-Bay biotech." },
      { name: "San Diego", blurb: "Sorrento Valley biotech, Kearny Mesa fleets, and Otay Mesa distribution." },
      { name: "Sacramento", blurb: "State agencies, Natomas offices, and Elk Grove distribution centers." },
    ],
    industries: [
      { slug: "offices-corporate", label: "Tech & Corporate Offices", text: "Bean-to-cup coffee, smart fridges, and healthy snack walls for 25+ employee sites." },
      { slug: "warehouses-manufacturing", label: "Warehouses & 3PL", text: "24/7 combo machines and micro-markets for Inland Empire and Central Valley DCs." },
      { slug: "hospitals-clinics", label: "Hospitals & Clinics", text: "HIPAA-friendly cashless machines for staff lounges and ED waiting areas." },
    ],
    extraFaqs: [
      {
        q: "Do you serve businesses across all of California?",
        a: "Yes — we have route operators across every California metro including Los Angeles, San Francisco, San Diego, Sacramento, Fresno, Bakersfield, San Jose, Oakland, Riverside, Long Beach, and Anaheim.",
      },
      {
        q: "How fast can you install a free vending machine in California?",
        a: "Most California sites are installed within 5–9 business days once your application is approved. Bay Area and LA metros are typically fastest.",
      },
    ],
  },
  texas: {
    slug: "texas",
    headline: "Texas Free Vending Machines — Statewide Coverage",
    intro:
      "Houston refineries, Dallas–Fort Worth logistics parks, Austin tech offices, and San Antonio manufacturing sites all run on our free vending program. Everything is DEER-friendly and heat-rated for Texas summers.",
    stats: [
      { label: "Metros served", value: "30+" },
      { label: "Machines placed statewide", value: "1,400+" },
      { label: "Avg. install window", value: "4–8 days" },
    ],
    topMetros: [
      { name: "Houston", blurb: "Energy corridor offices, Port of Houston 3PL, and Texas Medical Center." },
      { name: "Dallas", blurb: "DFW logistics, Legacy West offices, and Arlington entertainment district." },
      { name: "Austin", blurb: "Domain tech offices, Southeast Austin manufacturing, and Round Rock DCs." },
      { name: "San Antonio", blurb: "Military installations, medical center, and northeast manufacturing." },
    ],
    industries: [
      { slug: "warehouses-manufacturing", label: "Warehousing & Manufacturing", text: "Heat-rated combo machines built for TX distribution centers." },
      { slug: "offices-corporate", label: "Corporate Offices", text: "Coffee + snacks for Austin, DFW, and Houston HQs." },
      { slug: "military-bases", label: "Military Bases", text: "MWR-friendly programs for Lackland, Fort Cavazos, and JBSA." },
    ],
    extraFaqs: [
      {
        q: "Do you cover the Rio Grande Valley and West Texas?",
        a: "Yes — from El Paso and Midland-Odessa to McAllen and Brownsville, we have route partners statewide.",
      },
    ],
  },
  florida: {
    slug: "florida",
    headline: "Florida Free Vending Machines — Coast to Coast",
    intro:
      "Miami logistics, Orlando hospitality, Tampa healthcare, and Jacksonville port operations all qualify for our zero-cost vending program. Every FL machine is humidity-tolerant and hurricane-service-ready.",
    stats: [
      { label: "Metros served", value: "25+" },
      { label: "Machines placed statewide", value: "1,100+" },
      { label: "Avg. install window", value: "5–9 days" },
    ],
    topMetros: [
      { name: "Miami", blurb: "Doral warehousing, Brickell offices, and MIA-area logistics." },
      { name: "Orlando", blurb: "Attractions, hotels, and I-Drive hospitality." },
      { name: "Tampa", blurb: "Westshore offices, port distribution, and Brandon fulfillment." },
      { name: "Jacksonville", blurb: "JAXPORT logistics, southside offices, and Northside manufacturing." },
    ],
    industries: [
      { slug: "hotels-hospitality", label: "Hotels & Hospitality", text: "Guest-floor and staff BOH machines for FL resort and hotel groups." },
      { slug: "warehouses-manufacturing", label: "Warehouses & 3PL", text: "24/7 vending for Miami, Orlando, and Jacksonville DCs." },
      { slug: "airports-aviation", label: "Airports & Aviation", text: "Airside crew lounges at MIA, MCO, TPA, and FLL." },
    ],
    extraFaqs: [
      {
        q: "Do you service the Florida Panhandle and Keys?",
        a: "Yes — we have route coverage through Tallahassee, Pensacola, and down to Key Largo/Islamorada.",
      },
    ],
  },
  "new-york": {
    slug: "new-york",
    headline: "New York Free Vending Machines — NYC to Buffalo",
    intro:
      "Manhattan high-rises, Long Island warehouses, Westchester HQs, and Upstate manufacturing all qualify. We coordinate NYC building COIs and freight elevator scheduling on your behalf.",
    stats: [
      { label: "Metros served", value: "20+" },
      { label: "Machines placed statewide", value: "1,300+" },
      { label: "Avg. install window", value: "6–10 days" },
    ],
    topMetros: [
      { name: "New York City", blurb: "All 5 boroughs — from Midtown towers to Bronx logistics." },
      { name: "Buffalo", blurb: "Downtown offices, Cheektowaga fulfillment, and Amherst medical." },
      { name: "Rochester", blurb: "Henrietta manufacturing and downtown professional services." },
      { name: "Albany", blurb: "State agencies, capital region tech park, and healthcare campuses." },
    ],
    industries: [
      { slug: "offices-corporate", label: "Corporate Offices", text: "Coffee, snacks, and smart fridges for NYC and Westchester HQs." },
      { slug: "hospitals-clinics", label: "Hospitals & Clinics", text: "24/7 cashless machines for staff lounges across NY health systems." },
      { slug: "schools-universities", label: "Schools & Universities", text: "Compliant vending for SUNY, CUNY, and private campuses." },
    ],
    extraFaqs: [
      {
        q: "Do you handle NYC building COIs?",
        a: "Yes — we provide standard Certificates of Insurance for NYC building management and coordinate freight elevator windows before install.",
      },
    ],
  },
  illinois: {
    slug: "illinois",
    headline: "Illinois Free Vending Machines — Chicagoland & Downstate",
    intro:
      "Chicago Loop offices, O'Hare-area logistics, Naperville tech campuses, and downstate manufacturing all qualify. Union-friendly routes across the Chicagoland metro.",
    stats: [
      { label: "Metros served", value: "15+" },
      { label: "Machines placed statewide", value: "900+" },
      { label: "Avg. install window", value: "5–8 days" },
    ],
    topMetros: [
      { name: "Chicago", blurb: "Loop offices, O'Hare 3PL, and South Side manufacturing." },
      { name: "Naperville", blurb: "I-88 tech corridor and corporate campuses." },
      { name: "Rockford", blurb: "Aerospace manufacturing and distribution centers." },
      { name: "Springfield", blurb: "State agencies and healthcare systems." },
    ],
    industries: [
      { slug: "warehouses-manufacturing", label: "Warehousing & Manufacturing", text: "24/7 combo vending for Chicagoland DCs and downstate plants." },
      { slug: "offices-corporate", label: "Corporate Offices", text: "Loop and suburban HQ programs with coffee + snacks." },
    ],
    extraFaqs: [
      {
        q: "Are your Illinois route operators union-friendly?",
        a: "Yes — we regularly service union sites in Chicagoland and coordinate with building management for after-hours delivery and restocking.",
      },
    ],
  },
  pennsylvania: {
    slug: "pennsylvania",
    headline: "Pennsylvania Free Vending Machines — Philly, Pittsburgh & Beyond",
    intro:
      "Philadelphia logistics, Pittsburgh tech and manufacturing, Harrisburg government facilities, and Lehigh Valley fulfillment centers all qualify for zero-cost vending.",
    stats: [
      { label: "Metros served", value: "15+" },
      { label: "Machines placed statewide", value: "800+" },
      { label: "Avg. install window", value: "5–9 days" },
    ],
    topMetros: [
      { name: "Philadelphia", blurb: "Center City offices, Navy Yard logistics, and Northeast Philly DCs." },
      { name: "Pittsburgh", blurb: "Strip District tech, Oakland healthcare, and Monongahela Valley manufacturing." },
      { name: "Allentown", blurb: "Lehigh Valley fulfillment and I-78 corridor logistics." },
      { name: "Harrisburg", blurb: "State agencies, Hershey area, and Carlisle distribution." },
    ],
    industries: [
      { slug: "warehouses-manufacturing", label: "Warehouses & Fulfillment", text: "Combo machines for Lehigh Valley, Northeast Philly, and Central PA DCs." },
      { slug: "hospitals-clinics", label: "Hospitals & Health Systems", text: "24/7 vending for Penn Medicine, UPMC, and Geisinger sites." },
    ],
    extraFaqs: [],
  },
  georgia: {
    slug: "georgia",
    headline: "Georgia Free Vending Machines — Atlanta Metro & Statewide",
    intro:
      "Atlanta corporate HQs, Savannah port logistics, and Augusta military/medical campuses all qualify. Fast install windows across the ATL metro.",
    stats: [
      { label: "Metros served", value: "12+" },
      { label: "Machines placed statewide", value: "700+" },
      { label: "Avg. install window", value: "4–8 days" },
    ],
    topMetros: [
      { name: "Atlanta", blurb: "Buckhead and Midtown offices, Perimeter HQs, and I-85 logistics." },
      { name: "Savannah", blurb: "Port of Savannah 3PL and downtown hospitality." },
      { name: "Augusta", blurb: "Fort Eisenhower area and Medical College of Georgia." },
    ],
    industries: [
      { slug: "warehouses-manufacturing", label: "Warehousing & 3PL", text: "24/7 combos for ATL and Savannah distribution centers." },
      { slug: "airports-aviation", label: "Airports & Aviation", text: "Crew lounges and cargo areas at ATL and airfield operations." },
    ],
    extraFaqs: [],
  },
  arizona: {
    slug: "arizona",
    headline: "Arizona Free Vending Machines — Phoenix, Tucson & Beyond",
    intro:
      "Phoenix logistics, Tempe tech campuses, Mesa manufacturing, and Tucson defense contractors all qualify. Every AZ machine is heat-rated for 115°+ warehouse summers.",
    stats: [
      { label: "Metros served", value: "10+" },
      { label: "Machines placed statewide", value: "600+" },
      { label: "Avg. install window", value: "4–7 days" },
    ],
    topMetros: [
      { name: "Phoenix", blurb: "Deer Valley manufacturing, Sky Harbor logistics, and Downtown offices." },
      { name: "Tucson", blurb: "Defense, mining support, and University area offices." },
      { name: "Mesa", blurb: "East Valley manufacturing and Falcon Field aerospace." },
    ],
    industries: [
      { slug: "warehouses-manufacturing", label: "Warehousing & Manufacturing", text: "Heat-rated combo machines for Deer Valley and West Valley DCs." },
      { slug: "military-bases", label: "Military & Defense", text: "Programs for Luke AFB, DM AFB, and defense contractor sites." },
    ],
    extraFaqs: [],
  },
  "north-carolina": {
    slug: "north-carolina",
    headline: "North Carolina Free Vending Machines — Charlotte, RTP & Coast",
    intro:
      "Charlotte financial offices, Research Triangle Park tech and biotech, Greensboro logistics, and coastal military bases all qualify.",
    stats: [
      { label: "Metros served", value: "12+" },
      { label: "Machines placed statewide", value: "650+" },
      { label: "Avg. install window", value: "5–8 days" },
    ],
    topMetros: [
      { name: "Charlotte", blurb: "Uptown financial towers, University City, and Concord logistics." },
      { name: "Raleigh", blurb: "RTP tech and biotech campuses, downtown offices." },
      { name: "Greensboro", blurb: "Piedmont Triad logistics and manufacturing." },
    ],
    industries: [
      { slug: "offices-corporate", label: "Corporate Offices", text: "Coffee and healthy snacks for Charlotte and RTP campuses." },
      { slug: "hospitals-clinics", label: "Hospitals & Health Systems", text: "24/7 cashless vending for Duke, UNC, Novant, and Atrium sites." },
    ],
    extraFaqs: [],
  },
  ohio: {
    slug: "ohio",
    headline: "Ohio Free Vending Machines — Columbus, Cleveland, Cincinnati",
    intro:
      "Columbus logistics, Cleveland manufacturing and healthcare, Cincinnati corporate HQs, and Dayton aerospace all qualify. Statewide route coverage.",
    stats: [
      { label: "Metros served", value: "12+" },
      { label: "Machines placed statewide", value: "700+" },
      { label: "Avg. install window", value: "5–8 days" },
    ],
    topMetros: [
      { name: "Columbus", blurb: "Rickenbacker logistics, downtown offices, and OSU-area campuses." },
      { name: "Cleveland", blurb: "Cuyahoga manufacturing, Cleveland Clinic area, and Downtown offices." },
      { name: "Cincinnati", blurb: "Blue Ash corporate, CVG-area logistics, and Downtown HQs." },
    ],
    industries: [
      { slug: "warehouses-manufacturing", label: "Warehousing & Manufacturing", text: "Combos for Rickenbacker, CVG, and Northeast Ohio DCs." },
      { slug: "hospitals-clinics", label: "Hospitals & Clinics", text: "24/7 vending for Cleveland Clinic, UH, and OhioHealth sites." },
    ],
    extraFaqs: [],
  },
};

export const getStateHighlight = (slug: string) => STATE_HIGHLIGHTS[slug];
